import { makeObservable, observable, action, runInAction } from 'mobx';
import { IColumnVisibilityModel } from './Reports.store';
import { telemetryAPI } from '@api/Telemetry.api';
import Telemetry, { IRawTelemetry } from '@core/Telemetry';
import getDemoDate from '@utils/getDemoDate';
import { RootStore } from './Root.store';
import { ICol } from '@core/interfaces/ICol';
import { ISortModel } from '@core/interfaces/ISortModel';

export default class TelemetryStore {
    @observable
    public limit = 10;

    @observable
    public serverError: boolean;

    @observable
    public loading: boolean;

    @observable
    public isTelemetryVisible: boolean;

    @observable
    public selectedDevice: string | null;

    @observable
    public telemetryList: IRawTelemetry[];

    @observable
    public currentTelemetry: IRawTelemetry[];

    @observable
    public page: number;

    @observable
    public pagesNumber: number | null;

    @observable
    public columnVisibilityModel: IColumnVisibilityModel;

    @observable
    public sortModel: ISortModel[];

    @observable
    public active: boolean;

    constructor(private rootStore: RootStore) {
        this.setDefault();
        makeObservable(this);
    }

    @action
    setDefault() {
        let limit: number | void;
        let columnVisibilityModel: IColumnVisibilityModel | void;
        try {
            const columnVisibilityModelVal = localStorage.getItem(
                'columnVisibilityModel',
            );
            const limitVal = localStorage.getItem('deviceTelemetyLimit');
            if (columnVisibilityModelVal) {
                columnVisibilityModel = JSON.parse(columnVisibilityModelVal);
            }
            if (limitVal) {
                const data = parseInt(limitVal, 10);
                if (data > 0 && data <= 1000) limit = data;
            }
        } catch (e) {}
        this.loading = false;
        this.isTelemetryVisible = false;
        this.serverError = false;
        this.selectedDevice = null;
        this.telemetryList = [];
        this.currentTelemetry = [];
        this.page = 1;
        this.pagesNumber = null;
        this.columnVisibilityModel =
            columnVisibilityModel ??
            Telemetry.headers
                .map((field) => ({ field, val: true }))
                .reduce(
                    (res, item) => ({ ...res, [item['field']]: item.val }),
                    {},
                );
        this.sortModel = [];
        this.limit = limit ?? 50;
        this.active = false
    }

    @action
    async getTelemetry() {
        this.loading = true;
        this.telemetryList = [];
        const imei = this.selectedDevice;
        if (imei) {
            try {
                const token = this.rootStore.user.token;
                let from: number | void, to: number | void;
                let active = false;
                if (
                    this.rootStore.map.dateFilter !== 'customDate' &&
                    !this.rootStore.app.demoMode
                ) {
                    from = this.rootStore.map.fromDateFilter.getTime();
                    to = this.rootStore.map.toDateFilter.getTime();   
                }
                if (
                    this.rootStore.map.dateFilter == 'customDate' &&
                    !this.rootStore.app.demoMode
                ) {
                    from = this.rootStore.map.fromDateFilter.getTime();
                    to = this.rootStore.map.toDateFilter.getTime();
                }
                // if (this.rootStore.user.loginOldActivated) {
                //     active = true;
                //     //this.active = true;
                // };
                const limitRes = await telemetryAPI.getDeviceTelemetry({
                    token,
                    imei,
                    limit: 1,
                    from,
                    to,
                    active
                } as any);
                if (limitRes.result === 'error') {
                    runInAction(() => {
                        this.loading = false;
                        this.serverError = true;
                    });
                    return;
                }
                
                const maxLimit = 250;
                const length = limitRes.total;
                const limit = length > maxLimit ? maxLimit : length;
                let telemetryList: IRawTelemetry[] = [];
                const req = async (offset: number) => {
                    let res;
                    res = await telemetryAPI.getDeviceTelemetry({
                        token,
                        imei,
                        limit,
                        offset,
                        from,
                        to,
                        active
                    } as any);
                    if (res.result === 'error') {
                        runInAction(() => {
                            this.serverError = true;
                        });
                        return;
                    }
                    telemetryList = [...telemetryList, ...res.telemetry];
                };                
                const maxOffset =
                    length > maxLimit ? Math.floor(length / maxLimit) : 0;
                await Promise.all(
                    new Array(maxOffset + 1)
                        .fill(0)
                        .map(async (v, i) => await req(i * maxLimit)),
                );
                runInAction(() => {
                    this.loading = false;
                    if (this.selectedDevice === imei) {
                        if (this.rootStore.app.demoMode) {
                            telemetryList = telemetryList.map((item, i) => ({
                                ...item,
                                ts_d: getDemoDate(item.ts_d),
                            }));
                            this.telemetryList = [...telemetryList];
                        } else this.telemetryList = [...telemetryList];
                    }
                });
            } catch (e) {
                runInAction(() => {
                    this.loading = false;
                    this.serverError = true;
                });
            } 
        }
        
    }

    @action
    setPage(page: number) {
        this.page = page;
        const from = this.rootStore.map.fromDateFilter;
        const to = this.rootStore.map.toDateFilter;
        const fromMin = Math.floor(from.getTime() / 1000 / 60);
        const toMin = Math.floor(to.getTime() / 1000 / 60);
        const filteredTelemetry = this.telemetryList.filter(
            ({ ts_d, ts_s }) => {
                if (!ts_d && !ts_s) return false;
                const date = ts_d ? new Date(ts_d) : new Date(ts_s);
                const telemetryMin = Math.floor(date.getTime() / 1000 / 60);
                const isEarlierThanFrom = telemetryMin < fromMin;
                const isLaterThanTo = telemetryMin > toMin;
                return !(isEarlierThanFrom || isLaterThanTo);
            },
        );
        const total = filteredTelemetry.length;
        const limit = this.limit;
        this.pagesNumber = Math.ceil(total / limit);
        const offset = (page - 1) * limit;
        this.currentTelemetry = filteredTelemetry.slice(offset);
    }

    @action
    setSortModel(sortModel: ISortModel[]) {
        this.sortModel = sortModel;
    }

    @action
    resetSortModel() {
        this.sortModel = [];
    }

    @action
    setColumnVisibilityModel(model: ICol) {
        this.columnVisibilityModel = model;
    }

    @action
    hideCol(field: string) {
        if (Telemetry.headers.indexOf(field) > -1) {
            this.columnVisibilityModel = {
                ...this.columnVisibilityModel,
                [field]: false,
            };
        }
    }

    @action
    toggleCol(field: string) {
        if (Telemetry.headers.indexOf(field) > -1) {
            this.columnVisibilityModel = {
                ...this.columnVisibilityModel,
                [field]: !this.columnVisibilityModel[field],
            };
        }
    }

    @action
    showAllCol() {
        this.columnVisibilityModel = Telemetry.headers
            .map((field) => ({ field, val: true }))
            .reduce((res, item) => ({ ...res, [item['field']]: item.val }), {});
    }

    @action
    hideAllCol() {
        this.columnVisibilityModel = Telemetry.headers
            .map((field) => ({ field, val: false }))
            .reduce((res, item) => ({ ...res, [item['field']]: item.val }), {});
    }

    @action
    resetCurrentTelemetry() {
        this.currentTelemetry = [];
    }

    @action
    resetPage() {
        this.page = 1;
        this.pagesNumber = null;
    }

    @action
    resetServerError() {
        this.serverError = false;
    }

    @action
    setSelectedDevice(imei: string) {
        this.selectedDevice = imei;
    }

    @action
    resetSelectedDevice() {
        this.selectedDevice = null;
    }

    @action
    toggleTelemetryVisible() {
        this.isTelemetryVisible = !this.isTelemetryVisible;
    }

    @action
    setTelemetryVisible() {
        this.isTelemetryVisible = true;
    }

    @action
    resetTelemetryVisible() {
        this.isTelemetryVisible = false;
    }

    @action
    setLimit(limit: number) {
        this.limit = limit;
    }
}
